import { Skeleton, Avatar, Tooltip } from '@mantine/core';
import { useApiQuery } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { forwardRef } from 'react';

type Props = {
  appId: string;
  size?: number;
  tooltip?: string;
};

const IconById = forwardRef<HTMLDivElement, Omit<Props, 'tooltip'>>(
  ({ appId, size = 24 }, ref) => {
    const queryData = useApiQuery(
      async ($api, signal, args) => {
        const result = await $api.GET('/apps/{id}', {
          params: {
            path: {
              id: args.appId,
            },
          },
          signal,
        });

        if (result.error) throw result.error;

        const data = result.data;

        return {
          app: data,
        };
      },
      {
        skip: !appId,
        queryGroup: 'apps',
        uniqueQueryKey: `app-${appId}`,
        variables: {
          appId,
        },
        keepPreviousData: true,
        staleTime: Infinity,
      }
    );

    if (queryData.isLoading) return <Skeleton height={size} width={size} />;

    return queryData.data?.app.icon ? (
      <Avatar
        src={queryData.data.app.icon}
        size={size}
        ref={ref}
        styles={{
          placeholder: { visibility: 'hidden' },
        }}
        sx={(theme) => ({
          border: `1px solid ${theme.colors.gray[2]}`,
        })}
      />
    ) : (
      <Icon icon="ShareIcon" size={20} ref={ref} />
    );
  }
);

export const AppIconById = ({ tooltip, ...props }: Props) => {
  if (tooltip)
    return (
      <Tooltip label={tooltip}>
        <IconById {...props} />
      </Tooltip>
    );
  return <IconById {...props} />;
};
